/*
 * -----------------------------------------------------------------------
 *  <copyright file="phone-input.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import PI from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'hooks';
import './styles.scss';

const ReactPhoneInput = PI.default ? PI.default : PI;

interface ICustomPhoneInput {
    phone: string;
    onChange: Function;
    customId: string;
    label?: string;
    isValid: Function;
    disabled?: boolean;
    error?: string;
    customClassName?: string;
    dropdownClass?: string;
    required?: boolean;
    'data-testid'?: string;
}

const CustomPhoneInput = ({
    phone,
    onChange,
    customId,
    disabled,
    label,
    error,
    customClassName,
    dropdownClass,
    isValid,
    required,
    'data-testid': testId,
}: ICustomPhoneInput) => {
    const { t } = useTranslation();

    const [phoneNumber, setPhoneNumber] = useState('');
    const [country, setCountry] = useState(null);

    useEffect(() => {
        if (phone) setPhoneNumber(phone);
    }, [phone]);

    useEffect(() => {
        let errorNumber = null;
        const amountOfDigits = country?.dialCode.length + 7 ?? 10;
        if (phoneNumber.length > country?.dialCode.length && phoneNumber.length < amountOfDigits)
            errorNumber = t('uam:invalidCellMobileFormat');
        else if (country && phoneNumber.replace(country.dialCode, '').charAt(0) === '0')
            errorNumber = t('uam:invalidCellMobileFormat');
        else if (required && (phoneNumber.length <= (country?.dialCode.length ?? 4) || !phoneNumber))
            errorNumber = t('ra:requiredFieldError');

        isValid(errorNumber);

        // return always with no error
        // isValid(null)
    }, [country, phoneNumber, required]);

    return (
        <FormControl
            variant="outlined"
            className={`phone-input-control ${disabled ? 'disabled' : ''} ${customClassName || ''} ${
                error ? 'error' : ''
            }`}
            id={customId || 'custom-id'}
            data-testid={testId ?? customId}
        >
            <span className="phone-input-label">{label || t('uam:phone')}</span>
            <ReactPhoneInput
                country="gb"
                preferredCountries={['gb', 'us']}
                value={phoneNumber}
                enableLongNumbers
                disabled={disabled}
                onChange={(number: any, country: any, event: any, formatted: any) => {
                    setCountry(country);
                    setPhoneNumber(number);
                    onChange(number, country);
                }}
                placeholder={t('uam:enterPhone')}
                enableSearch
                countryCodeEditable={false}
                disableSearchIcon
                dropdownClass={dropdownClass}
                searchPlaceholder={t('uam:searchCountry')}
            />
            {!error && <p className="info-message">{t('uam:doNotIncludeLeadingNull')}</p>}
            {error && <p className="error-message">{error}</p>}
        </FormControl>
    );
};

export default React.memo(CustomPhoneInput);
